import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { EventLogResources } from '../mutators/util.js';
import { zql } from '../schema.js';
import { applyCursor } from '../util/apply-cursor.js';
import { Status } from '../util/schemas.js';
import { Cursor, Sort } from './util.js';
import { assertIsAuthenticated } from '../utils.js';
export const ListBasicDataFiltersInput = z.object({
    search: z.string().optional(),
    status: z.array(Status).optional(),
});
export const ListBasicDataInput = z.object({
    filters: ListBasicDataFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
export const ListDiagnosesFiltersInput = ListBasicDataFiltersInput.extend({ hasProtocols: z.boolean().optional() });
export const ListDiagnosesInput = ListBasicDataInput;
export const ListAdminRoutesFiltersInput = ListBasicDataFiltersInput;
export const ListAdminRoutesInput = ListBasicDataInput;
export const ListContainerTypesFiltersInput = ListBasicDataFiltersInput;
export const ListContainerTypesInput = ListBasicDataInput;
export const ListContainerMaterialsFiltersInput = ListBasicDataFiltersInput;
export const ListContainerMaterialsInput = ListBasicDataInput;
export const ListHazardClassesFiltersInput = ListBasicDataFiltersInput;
export const ListHazardClassesInput = ListBasicDataInput;
export const ListCompaniesFiltersInput = ListBasicDataFiltersInput;
export const ListCompaniesInput = ListBasicDataInput;
export const ListStockLocationsFiltersInput = ListBasicDataFiltersInput;
export const ListStockLocationsInput = ListBasicDataInput;
export const ListTreatmentChairsFiltersInput = ListBasicDataFiltersInput;
export const ListTreatmentChairsInput = ListBasicDataInput;
export const ListInsuranceCompaniesFiltersInput = ListBasicDataFiltersInput;
export const ListInsuranceCompaniesInput = ListBasicDataInput;
export const ListCategoriesFiltersInput = z.object({
    search: z.string().optional(),
    status: z.array(Status).optional(),
});
export const ListCategoriesInput = z.object({
    filters: ListCategoriesFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
function applyBasicDataFilters(q, filters) {
    if (!filters)
        return q;
    const { status } = filters;
    const search = filters.search?.trim();
    let wide = q;
    if (search) {
        // common pattern: search in code OR name OR description
        wide = wide.where(({ cmp, or }) => or(cmp('code', 'ILIKE', `%${search}%`), cmp('name', 'ILIKE', `%${search}%`), cmp('description', 'ILIKE', `%${search}%`)));
    }
    if (status?.length) {
        wide = wide.where('status', 'IN', status);
    }
    return wide;
}
function createBasicDataQuery(resource, args) {
    let q = resource.where('deletedAt', 'IS', null);
    q = applyBasicDataFilters(q, args.filters);
    q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
    return q;
}
function listBasicDataResource(resource) {
    return defineQuery(ListBasicDataInput, ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        return createBasicDataQuery(resource, args);
    });
}
function getBasicDataById(resource) {
    return defineQuery(z.string(), ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        return resource.related('created').where('id', '=', args).where('deletedAt', 'IS', null).one();
    });
}
function getBasicDataActivitiesById(resourceName) {
    return defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', resourceName)
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    });
}
export const basicDataQueries = {
    listDiagnoses: defineQuery(ListBasicDataInput.extend({ hasProtocols: z.boolean().optional() }), ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        let q = zql.diagnoses.where('deletedAt', 'IS', null);
        q = applyBasicDataFilters(q, args.filters);
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        if (args.hasProtocols) {
            q = q.where(({ exists }) => exists('protocols'));
        }
        return q;
    }),
    getDiagnosisById: getBasicDataById(zql.diagnoses),
    getDiagnosisActivitiesById: getBasicDataActivitiesById(EventLogResources.diagnosis),
    listAdminRoutes: listBasicDataResource(zql.adminRoutes),
    getAdminRouteById: getBasicDataById(zql.adminRoutes),
    getAdminRouteActivitiesById: getBasicDataActivitiesById(EventLogResources.administrationRoute),
    listApplicationForms: listBasicDataResource(zql.applicationForms),
    getApplicationFormById: getBasicDataById(zql.applicationForms),
    getApplicationFormActivitiesById: getBasicDataActivitiesById(EventLogResources.applicationForm),
    listContainerTypes: listBasicDataResource(zql.containerTypes),
    getContainerTypeById: getBasicDataById(zql.containerTypes),
    getContainerTypeActivitiesById: getBasicDataActivitiesById(EventLogResources.containerType),
    listContainerMaterials: listBasicDataResource(zql.containerMaterials),
    getContainerMaterialById: getBasicDataById(zql.containerMaterials),
    getContainerMaterialActivitiesById: getBasicDataActivitiesById(EventLogResources.containerMaterial),
    listHazardClasses: listBasicDataResource(zql.hazardClasses),
    getHazardClassById: getBasicDataById(zql.hazardClasses),
    getHazardClassActivitiesById: getBasicDataActivitiesById(EventLogResources.hazardClass),
    listSolutionTypes: listBasicDataResource(zql.solutionTypes),
    getSolutionTypeById: getBasicDataById(zql.solutionTypes),
    getSolutionTypeActivitiesById: getBasicDataActivitiesById(EventLogResources.hazardClass),
    listCompanies: listBasicDataResource(zql.companies),
    getCompanyById: getBasicDataById(zql.companies),
    getCompanyActivitiesById: getBasicDataActivitiesById(EventLogResources.company),
    listStockLocations: listBasicDataResource(zql.stockLocations),
    getStockLocationById: getBasicDataById(zql.stockLocations),
    getStockLocationActivitiesById: getBasicDataActivitiesById(EventLogResources.stockLocation),
    listTreatmentChairs: listBasicDataResource(zql.treatmentChairs),
    getTreatmentChairById: getBasicDataById(zql.treatmentChairs),
    getTreatmentChairActivitiesById: getBasicDataActivitiesById(EventLogResources.treatmentChair),
    listInsuranceCompanies: listBasicDataResource(zql.insuranceCompanies),
    getInsuranceCompanyById: getBasicDataById(zql.insuranceCompanies),
    getInsuranceCompanyActivitiesById: getBasicDataActivitiesById(EventLogResources.insuranceCompany),
    listDrugsCategories: defineQuery(ListCategoriesInput, ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        let q = zql.drugsCategories
            .related('children', children => children.where(({ and, cmp }) => {
            const conditions = [];
            // apply the filter status for the children
            if (args.filters?.status?.length)
                conditions.push(cmp('status', 'IN', args.filters.status));
            return and(...conditions);
        }))
            .related('drugCatalogItemsCategories', drugItemsCategories => drugItemsCategories
            .related('drugCatalogItem', drug => drug
            .where(({ and, cmp }) => {
            const conditions = [];
            // apply the filter status for the children
            if (args.filters?.status?.length)
                conditions.push(cmp('status', 'IN', args.filters.status));
            return and(...conditions);
        })
            .where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where(({ and, cmp }) => and(cmp('parentId', 'IS', null), cmp('deletedAt', 'IS', null)));
        if (args.filters) {
            const search = args.filters.search?.trim();
            // apply the search only for the parent category
            if (search) {
                q = q.where(({ or, cmp }) => or(cmp('name', 'ILIKE', `%${search}%`), cmp('description', 'ILIKE', `%${search}%`)));
            }
            if (args.filters.status?.length) {
                q = q.where('status', 'IN', args.filters.status);
            }
        }
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        return q;
    }),
    getDrugCategoryById: defineQuery(z.string(), ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        return zql.drugsCategories
            .related('children')
            .related('parent')
            .related('created')
            .related('drugCatalogItemsCategories', drugItemsCategories => drugItemsCategories
            .related('drugCatalogItem', drug => drug.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null))
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getDrugCategoryActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', EventLogResources.drugsCategory)
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    listMedicineForms: listBasicDataResource(zql.medicineForms),
    getMedicineFormById: getBasicDataById(zql.medicineForms),
    getMedicineFormActivitiesById: getBasicDataActivitiesById(EventLogResources.medicineForm),
    listGeneralPhysicians: listBasicDataResource(zql.generalPhysicians),
    getGeneralPhysicianById: getBasicDataById(zql.generalPhysicians),
    getGeneralPhysicianActivitiesById: getBasicDataActivitiesById(EventLogResources.generalPhysician),
    listCancerTypes: defineQuery(ListBasicDataInput.extend({ hasProtocols: z.boolean().optional() }), ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        let q = zql.cancerTypes.where('deletedAt', 'IS', null);
        q = applyBasicDataFilters(q, args.filters);
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        if (args.hasProtocols) {
            q = q.where(({ exists }) => exists('protocols'));
        }
        return q;
    }),
    getCancerTypeById: getBasicDataById(zql.cancerTypes),
    getCancerTypeActivitiesById: getBasicDataActivitiesById(EventLogResources.cancerType),
    listStorageLabels: listBasicDataResource(zql.storageLabels),
    getStorageLabelById: getBasicDataById(zql.storageLabels),
    getStorageLabelActivitiesById: getBasicDataActivitiesById(EventLogResources.storageLabel),
};
//# sourceMappingURL=basic-data.queries.js.map